import { useState } from "react";
import { Search, UserX, AlertTriangle, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function MissingStaffDiagnostic() {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    const q = name.trim();
    if (!q) { toast.error("Enter a name first"); return; }
    setBusy(true);
    setResult(null);
    try {
      const r = await api.get("/sync/campminder/diagnose", { params: { name: q } });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Diagnose failed");
    } finally {
      setBusy(false);
    }
  };

  const verdict = (() => {
    if (!result) return null;
    const inCm = (result.in_campminder_staff_cache || []).length > 0;
    const inApp = (result.in_crew_members || []).length > 0;
    const collisions = result.phone_collisions || [];

    if (inApp) return {
      tone: "ok",
      title: "Found in the app",
      msg: `${result.in_crew_members.length} match(es) already in the staff list. They should appear on Staff Cards.`,
    };
    if (!inCm) return {
      tone: "err",
      title: "Not returned by CampMinder",
      msg: "CampMinder's staff list (status=Hired) did NOT include anyone with that name. They're likely flagged as a different status in CampMinder (Pending, Returning Considered, Applicant, etc.). Have your CampMinder admin set their status to 'Hired' and re-sync.",
    };
    if (collisions.length > 0) {
      const c = collisions[0];
      return {
        tone: "warn",
        title: "Phone collision — overwritten by another staff member",
        msg: `${c.missing_name} shares phone ${c.phone} with ${c.currently_owned_by?.name || "another staff"} (${c.currently_owned_by?.division || ""}). The other person's CampMinder sync overwrites this one. Fix: give one of them a unique phone in CampMinder, OR ask the dev to switch the dedupe key to person_id.`,
      };
    }
    const cm = result.in_campminder_staff_cache[0] || {};
    if (cm.live_person_api && String(cm.live_person_api).startsWith("FAILED")) {
      return {
        tone: "err",
        title: "CampMinder Persons API failed for this person",
        msg: "They're in the staff list, but CampMinder couldn't return their contact details. Re-run the sync. If it keeps failing, their CampMinder Person record may be locked/private.",
      };
    }
    return {
      tone: "warn",
      title: "In CampMinder cache but not enriched",
      msg: "CampMinder returned them but they have no name/phone/email — so the sync skipped them. Add a phone or email to their CampMinder record and re-sync.",
    };
  })();

  const toneStyles = {
    ok: "bg-emerald-50 border-emerald-300 text-emerald-900",
    warn: "bg-amber-50 border-amber-300 text-amber-900",
    err: "bg-red-50 border-red-300 text-red-900",
  };
  const ToneIcon = verdict?.tone === "ok" ? CheckCircle2 : verdict?.tone === "warn" ? AlertTriangle : UserX;

  return (
    <div className="mt-6 pt-6 border-t border-slate-200" data-testid="missing-staff-diagnostic">
      <div className="flex items-center gap-2 mb-1"><Search className="w-4 h-4 text-slate-600" /><h3 className="font-display text-base font-bold">Find Missing Staff</h3></div>
      <p className="text-sm text-slate-500 mb-3">Type a name to find out why a CampMinder-hired staff member isn't showing in Staff Cards.</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") run(); }}
          placeholder="Last name or first name (e.g. peterson)"
          className="flex-1 w-full min-w-0 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
          data-testid="missing-staff-input"
        />
        <button
          onClick={run}
          disabled={busy}
          className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold text-sm w-full sm:w-auto"
          data-testid="missing-staff-search-btn"
        >
          {busy ? "Searching…" : "Diagnose"}
        </button>
      </div>

      {verdict && (
        <div className={`mt-4 p-4 rounded-xl border ${toneStyles[verdict.tone]}`} data-testid="missing-staff-verdict">
          <div className="flex items-start gap-2">
            <ToneIcon className="w-5 h-5 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold">{verdict.title}</div>
              <div className="text-sm mt-1">{verdict.msg}</div>
            </div>
          </div>
        </div>
      )}

      {result && (
        <details className="mt-3">
          <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">Show raw diagnostic data</summary>
          <pre className="mt-2 p-3 bg-slate-900 text-slate-100 text-xs rounded-lg overflow-auto max-h-80" data-testid="missing-staff-raw">{JSON.stringify(result, null, 2)}</pre>
        </details>
      )}
    </div>
  );
}
